import { useEffect } from 'react';
import { useThree } from '@react-three/fiber';

// Shifts the rendered scene sideways within the canvas via an asymmetric
// projection offset (camera.setViewOffset) — not camera rotation or
// position, so the viewing angle and CameraSync's orbit/target math are
// completely untouched, only the framing shifts. Built specifically so the
// Examples page's left-side legend overlay doesn't sit on top of the
// container — every other caller (console, Beyond CBM) passes no
// rightRatio, which is a full no-op (clearViewOffset, camera behaves
// exactly as before this component existed).
//
// Direction, worked through carefully after getting it backwards once:
// setViewOffset(fullWidth, fullHeight, offsetX, offsetY, width, height)
// renders only the sub-window [offsetX, offsetX+width] of a virtual sensor
// of size fullWidth. Centered content (world origin) always projects to
// the middle of that virtual sensor, fullWidth/2 — extending fullWidth to
// the RIGHT (offsetX stays 0) moves fullWidth/2 to the right of the
// window's own center (width/2), which is what pushes content right within
// the visible frame. Extending to the left (offsetX = extra) does the
// opposite — moves fullWidth/2 left of center, pushing content toward the
// left edge. That was the original bug: extending left instead of right.
export default function ViewOffset({ rightRatio = 0 }) {
  const { camera, size } = useThree();

  useEffect(() => {
    if (!rightRatio || !size.width || !size.height) {
      camera.clearViewOffset();
      return;
    }
    const extraRight = Math.round(size.width * rightRatio);
    const fullWidth = size.width + extraRight;
    camera.setViewOffset(fullWidth, size.height, 0, 0, size.width, size.height);
    camera.updateProjectionMatrix();
    return () => camera.clearViewOffset();
  }, [camera, size.width, size.height, rightRatio]);

  return null;
}
